import { useEffect } from "react";
import { useQueryClient, type QueryKey } from "@tanstack/react-query";
import { supabase } from "@lezz/supabase";

interface UseRealtimeInvalidationOptions {
  filter?: string;
  enabled?: boolean;
}

/**
 * Subscribes to Supabase Realtime postgres_changes for a given table and
 * invalidates the supplied React Query keys on any INSERT / UPDATE / DELETE.
 *
 * Pass a unique `channel` name per hook instance (e.g. include useId()) so
 * that removeChannel on unmount only tears down this subscription, not others
 * watching the same table.
 */
export function useRealtimeInvalidation(
  channel: string,
  table: string,
  queryKeys: QueryKey[],
  { filter, enabled = true }: UseRealtimeInvalidationOptions = {}
) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) return;

    const config = {
      event: "*" as const,
      schema: "public",
      table,
      ...(filter ? { filter } : {}),
    };

    const ch = supabase
      .channel(channel)
      .on("postgres_changes", config, () => {
        for (const key of queryKeys) {
          queryClient.invalidateQueries({ queryKey: key });
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(ch);
    };
    // queryKeys intentionally omitted — they are stable derivatives of `channel`
    // (which already encodes eventId / scope). Re-subscribing on array identity
    // change would be needless churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, table, filter, enabled, queryClient]);
}
